import { defineComponent as _defineComponent } from 'vue'
import {
  Beneficiary,
  beneficiaryName,
  AuthorityType,
  BeneficiaryStatus,
  ListSortParams,
  BeneficiaryManagerErrorCodes,
} from 'ah-api-gateways';
import BeneficiaryEditModal from './BeneficiaryEditModal.vue';
import BeneficiaryDetails from './BeneficiaryDetails.vue';
import { waitForEntityDeletion } from 'ah-requests';
import { BModal } from 'bootstrap-vue';
import DownloadStatementModal from 'ah-wallets/src/components/transactions/DownloadStatementModal.vue';
import SendMoneyToBeneficiaryModal from './SendMoneyToBeneficiaryModal.vue';
import TransactionsListing from 'ah-wallets/src/components/transactions/TransactionsListing.vue';
import { beneficiaryTransactionsLongTableFields } from 'ah-trades/src/models/transactionsTableFields';
import SortSelector from 'ah-common-lib/src/common/components/SortSelector.vue';
import BeneficiaryBankAccountDetails from './BeneficiaryBankAccountDetails.vue';
import { mergeMap } from 'rxjs/operators';
import { computed, reactive, ref, watch } from 'vue';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import { useAhBeneficiariesState } from '..';
import { useOnBehalfOf } from 'ah-common-lib/src/onBehalfOf/useInjectedOBO';


export default /*#__PURE__*/_defineComponent({
  __name: 'BeneficiaryInfo',
  props: {
    beneficiaryId: null,
    readonly: { type: [Boolean, String], default: false }
  },
  emits: ["back-to-list", "update:beneficiary", "beneficiary-deleted"],
  setup(__props: any, { emit }: { emit: ({
  (e: 'back-to-list'): void;
  (e: 'update:beneficiary', value: Beneficiary): void;
  (e: 'beneficiary-deleted'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { beneficiaryId: string, readonly: boolean | string };

const beneficiaryTransactions = ref<InstanceType<typeof TransactionsListing> | null>(null);

const deleteModal = ref<InstanceType<typeof BModal> | null>(null);





const state = reactive({
  beneficiary: null as Beneficiary | null,
  sortAndPageParams: {
    sort: beneficiaryTransactionsLongTableFields[0].key,
    sortDirection: 'DESC',
  } as Partial<ListSortParams>,
});

const requestManager = useRequestManager({
  clearOnDestroy: true,
  exposeToParent: ['getBeneficiary'],
  onRetryFromParentManager: (k: string) => {
    if (k === 'getBeneficiary') {
      loadBeneficiary();
    }
  },
});

const onBehalfOfClient = useOnBehalfOf();

const beneficiarieState = useAhBeneficiariesState();

const canManageBeneficiaries = computed(() => {
  return (
    onBehalfOfClient.value || beneficiarieState.store.useAuthStore().hasAuthorities(AuthorityType.MANAGE_BENEFICIARIES)
  );
});

const canSeeTransactions = computed(() => {
  return beneficiarieState.store.useAuthStore().hasAuthorities(AuthorityType.VIEW_TRANSACTIONS);
});

const fields = computed(() => {
  return beneficiaryTransactionsLongTableFields;
});

const isReadonly = computed(() => {
  return props.readonly !== false;
});

const config = computed(() => {
  return { tableFields: beneficiaryTransactionsLongTableFields };
});

function loadBeneficiary() {
  requestManager.manager
    .sameOrCancelAndNew(
      'getBeneficiary',
      beneficiarieState.services.beneficiary.getBeneficiary(props.beneficiaryId),
      props.beneficiaryId
    )
    .subscribe((response) => {
      state.beneficiary = response;
    });
}

watch(
  () => props.beneficiaryId,
  () => {
    loadBeneficiary();
  },
  { immediate: true }
);

function onFundsSent() {
  if (beneficiaryTransactions.value) {
    beneficiaryTransactions.value?.loadData();
  }
}

function goBack() {
  emit('back-to-list');
}

const findBeneficiaryName = computed(() => {
  return (state.beneficiary && beneficiaryName(state.beneficiary)) || '';
});

const canSendOwnMoney = computed(() => {
  return !onBehalfOfClient.value && beneficiarieState.store.useAuthStore().hasAuthorities(AuthorityType.MAKE_PAYMENTS);
});

const canSendOBOMoney = computed(() => {
  return onBehalfOfClient.value?.permissions?.includes(AuthorityType.PAY_ON_BEHALF_OF);
});

const shouldDisplaySendMoney = computed(() => {
  return (
    (canSendOwnMoney.value || canSendOBOMoney.value) &&
    !isReadonly.value &&
    state.beneficiary?.status === BeneficiaryStatus.APPROVED
  );
});

function onBeneficiaryUpdate(beneficiary: Beneficiary) {
  state.beneficiary = beneficiary;
  emit('update:beneficiary', beneficiary);
}

function confirmDeleteBeneficiary() {
  requestManager.manager
    .currentOrNew(
      `deleteBeneficiary-${props.beneficiaryId}`,
      beneficiarieState.services.beneficiary
        .deleteBeneficiary(props.beneficiaryId, {
          options: {
            errors: { silent: true },
          },
        })
        .pipe(
          mergeMap(() =>
            waitForEntityDeletion(
              () =>
                beneficiarieState.services.beneficiary.getBeneficiary(props.beneficiaryId, {
                  errors: { silent: true },
                }),
              { retries: 30 }
            )
          )
        )
    )
    .subscribe(
      () => {
        beneficiarieState.toast.success('Beneficiary successfully deleted');
        emit('beneficiary-deleted');
      },
      (e) => {
        const error = e.response?.data;
        if (error && error.code === BeneficiaryManagerErrorCodes.BUSINESS_RULE_NOT_MET && error.message) {
          beneficiarieState.toast.error(error.message);
        } else {
          beneficiarieState.toast.error('An unexpected problem has occurred. Please try again later.');
        }
      }
    );
}

function deleteBeneficiary() {
  deleteModal.value!.show();
}

const currency = computed(() => {
  return state.beneficiary?.currency;
});

const filter = computed(() => {
  return { otherBeneficiaryId: state.beneficiary!.id };
});

return { __sfc: true,beneficiaryTransactions, deleteModal, emit, props, state, requestManager, onBehalfOfClient, beneficiarieState, canManageBeneficiaries, canSeeTransactions, fields, isReadonly, config, loadBeneficiary, onFundsSent, goBack, findBeneficiaryName, canSendOwnMoney, canSendOBOMoney, shouldDisplaySendMoney, onBeneficiaryUpdate, confirmDeleteBeneficiary, deleteBeneficiary, currency, filter, BeneficiaryEditModal, BeneficiaryDetails, BModal, DownloadStatementModal, SendMoneyToBeneficiaryModal, TransactionsListing, SortSelector, BeneficiaryBankAccountDetails }
}

})